import { ClearOutlined, DownOutlined } from '@ant-design/icons';
import { DefaultOptionType } from '@bugly/common';
import { CharacterInput, CharacterInputValue, CustomInputRenderProps } from '@tencent/tds-ui';
import { useControllableValue } from 'ahooks';
import { Button, Dropdown, Form, InputNumber, InputNumberProps, SelectProps, Space, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwapRight from '@/assets/images/icons/SwapRight.react.svg?react';
import { MatchMode, NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { NUMBER_MAX_VALUE } from '@/presentation/components/tds-form/config';
import { TdsInput, TdsInputNumber } from '@/presentation/components/tds-form/tds-input';
import { TdsInputNumberRange } from '@/presentation/components/tds-form/tds-number-range';
import { BaseFormItemProps, PropsWithStyles } from '@/presentation/components/tds-form/type';
import styles from './index.module.scss';

export interface TdsNumberInputProps<T = any> extends BaseFormItemProps<T> {
  modeSelectProps?: SelectProps;
  valueSelectProps?: TdsNumberInputProps | InputNumberProps;
  modeOptions?: DefaultOptionType[];
  defaultActiveFirstMode?: boolean;
  dropDownMode?: boolean;
  showMode?: boolean;
}

// todo 应该改个名字
export const TdsNumberInput = (props: PropsWithStyles<TdsNumberInputProps>) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [value, setValue] = useControllableValue<CharacterInputValue>(props, {
    defaultValue: {
      mode: undefined,
      values: undefined,
    },
  });

  const ref = useRef<any>();
  const [filterText, setFilterText] = useState('');

  const {
    valueSelectProps,
    modeSelectProps,
    modeOptions = NUMBER_MATCH_OPTIONS,
    defaultActiveFirstMode = true,
    dropDownMode = false,
    showMode = true,
  } = props;

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const onConfirm = () => {
    // 修复：只获取表单字段的实际值，避免获取到原生的 values 方法
    const formValues = form.getFieldsValue();
    const { min, max } = formValues;

    hideOverlay();
    if (!min && !max) {
      setFilterText('');
    } else {
      setFilterText(`${min ?? ''}~${max ?? ''}`);
    }

    setValue({
      ...value,
      values: {
        min,
        max,
      },
    });
  };

  const overlay = (
    <div className={styles.customOverlay}>
      <div>
        <Form form={form} autoFocus>
          <Form.Item name='min' label='' noStyle>
            <InputNumber
              max={NUMBER_MAX_VALUE}
              controls={false}
            />
          </Form.Item>
          <>
            <SwapRight />
            <Form.Item name='max' label='' noStyle>
              <InputNumber
                max={NUMBER_MAX_VALUE}
                controls={false}
              />
            </Form.Item>
          </>
          <Tooltip title={t('重置')}>
            <ClearOutlined style={{ marginLeft: 12 }} onClick={() => form.resetFields()} />
          </Tooltip>
        </Form>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={hideOverlay} size='small'>{t('取消')}</Button>
          <Button type={'primary'} onClick={onConfirm} size='small'>{t('确定')}</Button>
        </Space>
      </div>
    </div>
  );

  const dropDownRender = () => (
    <div className={styles.selectRightLayer} style={{ position: 'relative', width: '100%' }}>
      <TdsInput
        placeholder={t('选择范围')}
        onFocus={showOverlay}
        suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
        style={{ width: '100%' }}
        value={filterText}
      />
      <Dropdown
        overlay={overlay}
        trigger={['click']}
        visible={visible}
        destroyPopupOnHide={false}
        onVisibleChange={visible => setVisible(visible)}
      >
        <div style={{ height: 1 }}/>
      </Dropdown>
    </div>
  );

  const customInputRender = (params: CustomInputRenderProps) => (value.mode === MatchMode.Between ? <TdsInputNumberRange
    {...valueSelectProps}
    value={params.value}
    onChange={params.onChange}
  /> : <TdsInputNumber
    className={'flex-1'}
    placeholder={t('请输入')}
    {...valueSelectProps}
    onChange={params.onChange}
    controls={false}
    value={params.value}
  />);

  useEffect(() => {
    if (!value?.values?.min && !value?.values?.max) {
      setFilterText('');
    } else {
      setFilterText(`${value?.values?.min ?? ''}~${value?.values?.max ?? ''}`);
    }
  }, [value]);

  return (
    <CharacterInput
      showMode={showMode}
      className={props.className}
      style={props.style}
      ref={ref}
      value={value}
      defaultActiveFirstMode={defaultActiveFirstMode}
      modeSelectProps={{
        dropdownMatchSelectWidth: 100,
        options: modeOptions,
        ...modeSelectProps,
      }}
      customInputRender={dropDownMode ? dropDownRender : customInputRender}
      onChange={(value) => {
        setValue(value);
      }}
    />
  );
};
